import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { api, CpdTeamLeader, CpdTeamOverviewResponse } from '../../services/api';
import {
  CpdKpiRow,
  CpdPerformanceDialog,
  CpdSectionCard,
  UserListFilter,
  cardSx,
  sectionTitleSx,
} from './CpdAnalyticsTab';

/**
 * The commission column reads as earnings here: a leader is looking at what their
 * agents have brought in this year, not at a company-wide commission report.
 */
const EARNINGS_LABEL = 'Earning This Year';

interface PerformanceDrill {
  title: string;
  subtitle: string;
  filter: UserListFilter;
}

function leaderOptionLabel(leader: CpdTeamLeader): string {
  const name = leader.name || leader.ceaNumber;
  return `${name} · ${leader.ceaNumber}`;
}

interface TeamCpdTabProps {
  /** Admins may view any eligible leader's team; leaders only see their own. */
  isAdmin: boolean;
}

/**
 * "Team CPD Status" — how much of one district director's team has cleared CPD.
 * The team is their `User.ManagerUserId` downline scoped to their own division, so
 * an agent who branched out to head another division is not counted against them.
 *
 * One bar, cleared vs ongoing. Clicking the Ongoing side lists the agents who have
 * not cleared, scored against what they have earned this year.
 */
const TeamCpdTab: React.FC<TeamCpdTabProps> = ({ isAdmin }) => {
  const [leaders, setLeaders] = useState<CpdTeamLeader[]>([]);
  const [selectedLeader, setSelectedLeader] = useState<CpdTeamLeader | null>(null);
  const [leadersError, setLeadersError] = useState<string | null>(null);
  const [data, setData] = useState<CpdTeamOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drill, setDrill] = useState<PerformanceDrill | null>(null);

  // The picker is the only reason to know about other leaders; a leader's own team
  // needs no lookup because the server resolves it from their session.
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getCpdTeamLeaders();
        if (!cancelled) setLeaders(res.leaders);
      } catch (err) {
        if (!cancelled) {
          setLeadersError(err instanceof Error ? err.message : 'Failed to load team leaders');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const teamCea = selectedLeader?.ceaNumber;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getCpdTeamOverview(teamCea);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Team CPD Status');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [teamCea]);

  useEffect(() => {
    load();
  }, [load]);

  // Once the leader list arrives, show which team the server defaulted to so the
  // picker is never blank while a team is on screen.
  useEffect(() => {
    if (selectedLeader || !data?.leader.ceaNumber || leaders.length === 0) return;
    const match = leaders.find((l) => l.ceaNumber === data.leader.ceaNumber);
    if (match) setSelectedLeader(match);
  }, [leaders, data?.leader.ceaNumber, selectedLeader]);

  const teamName = data?.leader.name || data?.leader.ceaNumber || 'My team';
  const divisionName = data?.leader.division || 'their division';

  const openDrill = useCallback(() => {
    if (!data?.leader.ceaNumber) return;
    setDrill({
      title: `${teamName}${data.leader.division ? ` · ${data.leader.division}` : ''}`,
      subtitle: `${teamName}${data.leader.division ? ` · ${data.leader.division}` : ''}`,
      filter: {
        status: 'Ongoing',
        snapshot: 'current',
        anyOutstanding: true,
        team: data.leader.ceaNumber,
      },
    });
  }, [data?.leader.ceaNumber, data?.leader.division, teamName]);

  return (
    <Box>
      {isAdmin && (
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Typography variant="subtitle2" sx={sectionTitleSx}>
            Team
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', display: 'block', mb: 1 }}
          >
            Pick a district director to see exactly what they see. Leaders only get their own team.
          </Typography>
          <Autocomplete
            size="small"
            options={leaders}
            value={selectedLeader}
            onChange={(_, next) => setSelectedLeader(next)}
            getOptionLabel={leaderOptionLabel}
            isOptionEqualToValue={(option, value) => option.ceaNumber === value.ceaNumber}
            renderOption={(props, option) => (
              <li {...props} key={option.ceaNumber}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {option.name || option.ceaNumber}
                    {option.division ? ` · ${option.division}` : ''}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                    {option.ceaNumber}
                    {option.designation ? ` · ${option.designation}` : ''}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search by name or CEA…" label="District director" />
            )}
            sx={{ maxWidth: 460 }}
          />
          {leadersError && (
            <Alert severity="warning" sx={{ mt: 1.5 }} onClose={() => setLeadersError(null)}>
              {leadersError}
            </Alert>
          )}
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 1.5,
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
          {data?.lastUploadedAt
            ? `Last updated ${new Date(data.lastUploadedAt).toLocaleString()}`
            : 'No CPD data uploaded yet'}
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={load} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !data ? (
        <Typography
          variant="body2"
          sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 6, textAlign: 'center' }}
        >
          No team CPD data to display.
        </Typography>
      ) : (
        <Stack spacing={2}>
          <Paper elevation={0} sx={cardSx}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={sectionTitleSx}>
                  {teamName}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                  {data.leader.designation ? `${data.leader.designation} · ` : ''}
                  {data.leader.ceaNumber}
                  {data.leader.division ? ` · ${data.leader.division}` : ''}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {data.team.members === 0 ? (
            <Alert severity="info">
              No agents report up to {teamName} within {divisionName}, so there is no team to report
              on.
            </Alert>
          ) : data.team.inSnapshot === 0 ? (
            <Alert severity="info">
              None of the {data.team.members.toLocaleString()} agents in this team appear in the
              current CPD upload, so there is nothing to report yet.
            </Alert>
          ) : (
            <CpdSectionCard
              title="Team CPD fulfilment"
              subtitle="CPD fulfilment across the whole team · the ring's Passed % counts exempted as cleared"
            >
              <CpdKpiRow
                current={data.statusCounts}
                lastWeek={data.statusCounts}
                hasLastWeekSnapshot={false}
                onOngoingClick={openDrill}
                ongoingHint="Tap to see more details"
              />
            </CpdSectionCard>
          )}
        </Stack>
      )}

      <CpdPerformanceDialog
        open={drill !== null}
        title={drill?.title ?? ''}
        subtitle={drill?.subtitle}
        hideHeaderSubtitle
        filter={drill?.filter ?? null}
        fixedYearToDate
        incomeLabel={EARNINGS_LABEL}
        showIncome={false}
        allowTemplateEdit={false}
        onClose={() => setDrill(null)}
      />
    </Box>
  );
};

export default TeamCpdTab;
